import { Tooltip } from 'shared/ui/Tooltip';
import classNames from 'classnames';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { BaseCheckbox } from 'shared/ui/Checkbox';

import { useHolidaysActions } from './store/store';
import { getCheckboxName } from './lib';
import type { CustomHoliday, Holiday } from './model';
import { RemoveButton } from './RemoveButton';

interface SchedulingDayChangeableProps {
  className?: string;
  holiday: Holiday | CustomHoliday;
}

export function Day({ holiday, className }: SchedulingDayChangeableProps) {
  const { day, month, year, description, weekDay, isAvailable } = holiday;
  const name = getCheckboxName(holiday.year, holiday.month, holiday.day);

  const { reverseIsChecked, removeHoliday } = useHolidaysActions();

  const onRemoveClick = () => {
    removeHoliday(holiday);
  };

  const onCheckboxClick = () => {
    reverseIsChecked(holiday);
  };

  return (
    <div className={classNames('flex justify-start items-end', className)}>
      <Tooltip
        content={
          <div className="text-start">
            This date cannot be removed or added. Please contact your partnerships manager.
          </div>
        }
        placement="top"
        widthClass="max-w-[291px]"
        contentClass="!pl-3 !pr-2"
        isDisabled={isAvailable}
        dataTestId={`tooltip-wrapper-${name}`}
        {...getDataAutoTestAttributes([`tooltip-content-${name}`])}
      >
        <BaseCheckbox
          controlledProps={{ name, checked: holiday.isChecked }}
          className={classNames('pb-[2px]', {
            '!cursor-not-allowed': !isAvailable,
          })}
          onChange={onCheckboxClick}
          isChecked={holiday.isChecked}
          isDisabled={!holiday.isAvailable}
          iconClassName={!holiday.isAvailable ? 'text-slate-200' : ''}
        />
      </Tooltip>
      <Tooltip
        content={<div className="text-left text-xs">{description}</div>}
        placement="top-start"
        isDisabled={!description}
      >
        <div className={classNames('text-sm select-none', className)}>
          {`${month} ${day}, ${year}`}
          <span className="ml-1 text-slate-300">({weekDay})</span>
        </div>
      </Tooltip>
      {holiday.isCustom && (
        <RemoveButton
          className="ml-[9px] pb-[6px]"
          isDisabled={!holiday.isAvailable}
          onClick={onRemoveClick}
          date={name}
        />
      )}
    </div>
  );
}
